import { MetalPlanData } from '@/page/domain/MetalPlanData';
import { MetalPlanRepository } from '@/page/domain/MetalPlanRepository';
import { SfApiResponse, useTranslation } from '@hfc/ui-core';

const { t } = useTranslation();

export class MetalPlanMemory implements MetalPlanRepository {

  public async getPlans (): Promise<SfApiResponse<Array<MetalPlanData>>> {
    const metalPlans: Array < MetalPlanData > = [
      {
        'key': 'bronze_60',
        'primary': true,
        'showMedicalCosts': true,
        'priceRating': 25,
        'popularityRating': 80,
        'tierNumber': 60,
        'tierName': t('metalPlans.tiers.bronze'),
        'coverage': 60,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$5.800', family: '$11.600' }),
        'doctorVisits': t('metalPlans.values.primaryDoctor', { amount: '$60' }),
        'genericDrugs': '$20',
        'hospital': t('metalPlans.values.afterDeductibleUpToOopMax', { percent: '40%' }),
        'icon': '/assets/img/plans/bronze.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$11.650', family: '$23.300' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'bronze_60_hsa',
        'primary': false,
        'priceRating': 25,
        'popularityRating': 40,
        'tierSuffix': 'HSA',
        'tierNumber': 60,
        'tierName': t('metalPlans.tiers.bronze'),
        'coverage': 60,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$7.800', family: '$15.600' }),
        'doctorVisits': `${t('metalPlans.values.preventativeFree')} <br> ${t('metalPlans.values.allOtherServicesFullPrice')}`,
        'genericDrugs': t('metalPlans.values.fullPriceUntilOopMax'),
        'hospital': t('metalPlans.values.fullPriceUntilOopMax'),
        'icon': '/assets/img/plans/bronze.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$7.800', family: '$15.600' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'silver_70',
        'primary': true,
        'showMedicalCosts': true,
        'priceRating': 50,
        'popularityRating': 100,
        'tierNumber': 70,
        'tierName': t('metalPlans.tiers.silver'),
        'coverage': 70,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$4.700', family: '$9.400' }),
        'doctorVisits': '$50 - $100',
        'genericDrugs': '$20',
        'hospital': t('metalPlans.values.afterDeductibleUpToOopMax', { percent: '30%' }),
        'icon': '/assets/img/plans/silver.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$11.650', family: '$23.300' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'silver_73',
        'primary': false,
        'priceRating': 50,
        'popularityRating': 100,
        'tierNumber': 73,
        'tierName': t('metalPlans.tiers.silver'),
        'coverage': 70,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$4.700', family: '$9.400' }),
        'doctorVisits': '$50 - $100',
        'genericDrugs': '$20',
        'hospital': t('metalPlans.values.afterDeductibleUpToOopMax', { percent: '30%' }),
        'icon': '/assets/img/plans/silver.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$9.600', family: '$19.200' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'silver_87',
        'primary': false,
        'priceRating': 25,
        'popularityRating': 100,
        'tierNumber': 87,
        'tierName': t('metalPlans.tiers.silver'),
        'coverage': 70,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$1.100', family: '$2.200' }),
        'doctorVisits': '$15 - $30',
        'genericDrugs': '$10',
        'hospital': t('metalPlans.values.afterDeductibleUpToOopMax', { percent: '20%' }),
        'icon': '/assets/img/plans/silver.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$4.000', family: '$8.000' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'silver_94',
        'primary': false,
        'priceRating': 25,
        'popularityRating': 100,
        'tierNumber': 94,
        'tierName': t('metalPlans.tiers.silver'),
        'coverage': 70,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$200', family: '$400' }),
        'doctorVisits': '$5 - $8',
        'genericDrugs': '$3',
        'hospital': t('metalPlans.values.afterDeductibleUpToOopMax', { percent: '10%' }),
        'icon': '/assets/img/plans/silver.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$3.000', family: '$6.000' }),
        'medical': {
          'coverage': 70,
        }
      },
      {
        'key': 'gold_80',
        'primary': true,
        'showMedicalCosts': true,
        'priceRating': 75,
        'popularityRating': 20,
        'tierNumber': 80,
        'tierName': t('metalPlans.tiers.gold'),
        'coverage': 80,
        'deductible': t('metalPlans.none'),
        'doctorVisits': '$40 - $80',
        'genericDrugs': '$19',
        'hospital': `${t('metalPlans.values.perDayUpToFiveDays', { amount: '$375' })} <br>${t('metalPlans.values.mostHmos')} <br>${t('metalPlans.values.ppoEpoSomeHmos', { percent: '30%' })}`,
        'icon': '/assets/img/plans/gold.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$9.600', family: '$19.200' }),
        'medical': {
          'coverage': 80,
        }
      },
      {
        'key': 'platinum_90',
        'primary': true,
        'showMedicalCosts': true,
        'priceRating': 100,
        'popularityRating': 40,
        'tierNumber': 90,
        'tierName': t('metalPlans.tiers.platinum'),
        'coverage': 90,
        'deductible': t('metalPlans.none'),
        'doctorVisits': '$20 - $45',
        'genericDrugs': '$10',
        'hospital': `${t('metalPlans.values.perDayUpToFiveDays', { amount: '$325' })} ${t('metalPlans.values.mostHmos')} <br>${t('metalPlans.values.ppoEpoSomeHmos', { percent: '10%' })}`,
        'icon': '/assets/img/plans/platinum.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$5.500', family: '$11.000' }),
        'medical': {
          'coverage': 90,
        }
      },
      {
        'key': 'catastrophic',
        'primary': false,
        'tierName': t('metalPlans.tiers.catastrophic'),
        'coverage': 60,
        'icon': '/assets/img/plans/catastrophic.webp',
      },
      {
        'key': 'minimum_coverage',
        'primary': false,
        'showMedicalCosts': true,
        'priceRating': 25,
        'popularityRating': 40,
        'tierName': t('metalPlans.tiers.minimumCoverage'),
        'coverage': 0,
        'deductible': t('metalPlans.values.individualFamily', { individual: '$12.000', family: '$24.000' }),
        'doctorVisits': t('metalPlans.values.firstThreeVisitsFree'),
        'genericDrugs': t('metalPlans.values.fullPriceUntilOopMax'),
        'hospital': t('metalPlans.values.fullPriceUntilOopMax'),
        'icon': '/assets/img/plans/minimum_coverage.webp',
        'oopMax': t('metalPlans.values.individualFamily', { individual: '$12.000', family: '$24.000' }),
        'medicalCosts': {
          'companyPays': '0%*',
          'youPay': '100%**',
          'notes': [
            `* ${t('metalPlans.values.untilOopMaxIsMet')}`,
            `** ${t('metalPlans.values.upToOopMaxMedicalCosts')}`,
          ],
        }
      }
    ];

    return {
      success: true,
      data: metalPlans
    };
  }

}
